import type { CalendarDocV2 } from '../model/types';

const DB_NAME = 'CalendarDraftV2';
const STORE = 'drafts';
const KEY = 'current';
const V1_DB = 'CalendarProjectDB';
const V1_STORE = 'autosave';
const V1_KEY = 'calendar_project_autosave';

const DAY = 24 * 60 * 60 * 1000;
export const DRAFT_EXPIRATION_MS = 7 * DAY;
/** Storage deletes un-attached uploads after ~7 days; treat them as gone at 6. */
export const UPLOAD_STALE_MS = 6 * DAY;

export interface DraftKV {
  get(key: string): Promise<unknown | undefined>;
  put(key: string, value: unknown): Promise<void>;
  delete(key: string): Promise<void>;
}

export interface DraftPhoto {
  localId: string;
  key?: string;
  uploadedAt?: number;
  previewBlob: Blob;
  w: number;
  h: number;
}

export interface DraftOptions {
  kv?: DraftKV;
  now?: () => number;
}

interface StoredDraft {
  savedAt: number;
  doc: CalendarDocV2;
  photos: DraftPhoto[];
}

/**
 * Small IndexedDB adapter; opens a connection per call, like v1.
 * With create=false (read-only access to someone else's DB) it never creates
 * the database: a missing DB is detected via databases() and, failing that, by
 * aborting the upgrade transaction.
 */
export function createIdbKV(
  dbName: string,
  storeName: string,
  create = true,
  idb: IDBFactory = indexedDB
): DraftKV {
  const open = async (): Promise<IDBDatabase | null> => {
    if (!create && typeof idb.databases === 'function') {
      const list = await idb.databases();
      if (!list.some((d) => d.name === dbName)) return null;
    }
    return new Promise((resolve, reject) => {
      const req = idb.open(dbName);
      req.onerror = () => reject(req.error);
      req.onsuccess = () => resolve(req.result);
      req.onupgradeneeded = () => {
        if (!create) {
          req.transaction?.abort();
          return;
        }
        if (!req.result.objectStoreNames.contains(storeName)) {
          req.result.createObjectStore(storeName);
        }
      };
    });
  };
  const run = async <T>(
    mode: IDBTransactionMode,
    fn: (s: IDBObjectStore) => IDBRequest<T>
  ): Promise<T | undefined> => {
    const db = await open();
    if (!db) return undefined;
    try {
      if (!db.objectStoreNames.contains(storeName)) return undefined;
      return await new Promise<T>((resolve, reject) => {
        const tx = db.transaction(storeName, mode);
        const req = fn(tx.objectStore(storeName));
        // Writes are only durable once the transaction completes (it can
        // still abort on commit, e.g. quota exceeded).
        tx.oncomplete = () => resolve(req.result);
        tx.onabort = () => reject(tx.error ?? req.error);
        tx.onerror = () => reject(tx.error ?? req.error);
      });
    } finally {
      db.close();
    }
  };
  return {
    get: (k) => run('readonly', (s) => s.get(k)),
    put: async (k, v) => {
      await run('readwrite', (s) => s.put(v, k));
    },
    delete: async (k) => {
      await run('readwrite', (s) => s.delete(k));
    },
  };
}

const defaultKV = (): DraftKV => createIdbKV(DB_NAME, STORE);

export async function saveDraftV2(
  doc: CalendarDocV2,
  photos: DraftPhoto[],
  opts: DraftOptions = {}
): Promise<void> {
  const kv = opts.kv ?? defaultKV();
  const now = opts.now ?? Date.now;
  const stored: StoredDraft = {
    savedAt: now(),
    doc,
    photos: photos.map((p) => ({
      localId: p.localId,
      key: p.key,
      uploadedAt: p.uploadedAt,
      previewBlob: p.previewBlob,
      w: p.w,
      h: p.h,
    })),
  };
  await kv.put(KEY, stored);
}

export async function loadDraftV2(opts: DraftOptions = {}): Promise<{
  doc: CalendarDocV2;
  photos: DraftPhoto[];
  staleLocalIds: string[];
} | null> {
  const kv = opts.kv ?? defaultKV();
  const now = (opts.now ?? Date.now)();
  const s = (await kv.get(KEY)) as StoredDraft | undefined;
  if (!s) return null;
  if (now - s.savedAt > DRAFT_EXPIRATION_MS) {
    await kv.delete(KEY);
    return null;
  }
  const staleLocalIds: string[] = [];
  const photos = s.photos.map((p) => {
    if (
      p.key &&
      p.uploadedAt !== undefined &&
      now - p.uploadedAt > UPLOAD_STALE_MS
    ) {
      staleLocalIds.push(p.localId);
      const { key: _key, ...rest } = p;
      return rest as DraftPhoto;
    }
    return p;
  });
  return { doc: s.doc, photos, staleLocalIds };
}

export async function clearDraftV2(opts: DraftOptions = {}): Promise<void> {
  await (opts.kv ?? defaultKV()).delete(KEY);
}

/** True when the old (v1) builder has an autosave younger than 7 days. Read-only. */
export async function hasV1Draft(
  opts: DraftOptions & { v1kv?: DraftKV } = {}
): Promise<boolean> {
  try {
    const kv = opts.v1kv ?? createIdbKV(V1_DB, V1_STORE, false);
    const v = (await kv.get(V1_KEY)) as { timestamp?: string } | undefined;
    if (!v) return false;
    const age = (opts.now ?? Date.now)() - new Date(v.timestamp ?? 0).getTime();
    return age <= DRAFT_EXPIRATION_MS;
  } catch {
    return false;
  }
}
